/**
 * Laser cut-length measurement: inkex-style baked path length + clip intersect.
 * Filled contours are summed individually (outer + holes); they are not unioned.
 */

import SVGPathCommander from 'svg-path-commander';
import { measureTransformedPathLength } from './pathSegmentLength';
import { parseSvgViewBox } from './svgDocument';
import { transformPathDataWithMatrix, type SvgMatrixLike } from './svgPathTransform';

type PathD = { x: number; y: number }[];
type PathsD = PathD[];

type ClipperModule = typeof import('clipper2-ts');

let clipperModulePromise: Promise<ClipperModule> | null = null;

function loadClipper(): Promise<ClipperModule> {
  if (!clipperModulePromise) {
    clipperModulePromise = import('clipper2-ts');
  }
  return clipperModulePromise;
}

const GEOMETRY_TAGS = new Set(['path', 'circle', 'ellipse', 'rect', 'line', 'polyline', 'polygon']);
const GEOMETRY_SELECTOR = 'path,circle,ellipse,rect,line,polyline,polygon';

function isGeometryTag(el: Element): boolean {
  return GEOMETRY_TAGS.has(el.tagName.toLowerCase());
}

function asGeometryElement(el: Element): SVGGeometryElement {
  return el as SVGGeometryElement;
}

function isClipPathElement(el: Element): el is SVGClipPathElement {
  return el.tagName.toLowerCase() === 'clippath';
}

function isGraphicsElement(el: Element): el is SVGGraphicsElement {
  const tag = el.tagName.toLowerCase();
  return GEOMETRY_TAGS.has(tag) || tag === 'g' || tag === 'svg' || tag === 'use';
}
const NON_RENDERED_ANCESTOR_TAGS = new Set(['defs', 'clippath', 'mask', 'symbol', 'metadata', 'title', 'desc']);

const MEASURE_MOUNT_STYLES =
  'position:absolute;left:-9999px;top:-9999px;overflow:hidden;pointer-events:none';

const IDENTITY_MATRIX: SvgMatrixLike = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

type SvgTransformTarget = SVGGraphicsElement & {
  getTransformToElement(target: SVGElement): SvgMatrixLike;
};

function supportsTransformToElement(geom: SVGGraphicsElement): geom is SvgTransformTarget {
  return typeof (geom as SvgTransformTarget).getTransformToElement === 'function';
}

function parseTransformMatrix(_root: SVGSVGElement, transformAttr: string | null): SvgMatrixLike {
  if (!transformAttr?.trim()) return IDENTITY_MATRIX;

  const DOMMatrixCtor = (globalThis as typeof globalThis & { DOMMatrix?: typeof DOMMatrix }).DOMMatrix;
  if (DOMMatrixCtor) {
    try {
      const matrix = new DOMMatrixCtor(transformAttr);
      return { a: matrix.a, b: matrix.b, c: matrix.c, d: matrix.d, e: matrix.e, f: matrix.f };
    } catch {
      // fall through
    }
  }

  try {
    const CSSMatrixCtor = SVGPathCommander.CSSMatrix as unknown as typeof DOMMatrix;
    const matrix = new CSSMatrixCtor(transformAttr);
    return { a: matrix.a, b: matrix.b, c: matrix.c, d: matrix.d, e: matrix.e, f: matrix.f };
  } catch {
    return IDENTITY_MATRIX;
  }
}

function getGeometryToRootMatrix(geom: SVGGraphicsElement, root: SVGSVGElement): SvgMatrixLike {
  try {
    if (supportsTransformToElement(geom)) {
      return geom.getTransformToElement(root);
    }
    const geomCtm = geom.getCTM?.();
    const rootCtm = root.getCTM?.();
    if (geomCtm && rootCtm) {
      return rootCtm.inverse().multiply(geomCtm);
    }
  } catch {
    // fall through to attribute chain
  }

  let matrix = IDENTITY_MATRIX;
  let node: Element | null = geom;
  while (node && node !== root) {
    matrix = multiplyMatrices(parseTransformMatrix(root, node.getAttribute('transform')), matrix);
    node = node.parentElement;
  }
  return matrix;
}

function multiplyMatrices(a: SvgMatrixLike, b: SvgMatrixLike): SvgMatrixLike {
  return {
    a: a.a * b.a + a.c * b.b,
    b: a.b * b.a + a.d * b.b,
    c: a.a * b.c + a.c * b.d,
    d: a.b * b.c + a.d * b.d,
    e: a.a * b.e + a.c * b.f + a.e,
    f: a.b * b.e + a.d * b.f + a.f,
  };
}

function transformPoint(matrix: SvgMatrixLike, x: number, y: number): { x: number; y: number } {
  return {
    x: matrix.a * x + matrix.c * y + matrix.e,
    y: matrix.b * x + matrix.d * y + matrix.f,
  };
}

function parseTransformOnRoot(root: SVGSVGElement, transformAttr: string | null): SvgMatrixLike {
  return parseTransformMatrix(root, transformAttr);
}

function objectBoundingBoxMatrix(element: SVGGraphicsElement): SvgMatrixLike {
  const bbox = element.getBBox();
  return { a: bbox.width, b: 0, c: 0, d: bbox.height, e: bbox.x, f: bbox.y };
}

function resolveUrlId(value: string | null): string | null {
  if (!value) return null;
  const match = value.match(/url\s*\(\s*['"]?#([^'")\s]+)/i);
  return match?.[1] ?? null;
}

function readStyleProperty(style: string, name: string): string | null {
  const match = style.match(new RegExp(`(?:^|;)\\s*${name}\\s*:\\s*([^;]+)`, 'i'));
  return match?.[1]?.trim() ?? null;
}

function hasPaintedFillFromAttrs(el: Element): boolean {
  const fill = el.getAttribute('fill');
  if (fill === 'none' || fill === 'transparent') return false;
  if (fill) return true;
  const styleFill = readStyleProperty(el.getAttribute('style') ?? '', 'fill');
  if (styleFill === 'none' || styleFill === 'transparent') return false;
  if (styleFill) return true;
  return true;
}

function hasPaintedStrokeFromAttrs(el: Element): boolean {
  const stroke = el.getAttribute('stroke');
  if (stroke === 'none' || stroke === 'transparent') return false;
  if (stroke) return true;
  const styleStroke = readStyleProperty(el.getAttribute('style') ?? '', 'stroke');
  if (styleStroke === 'none' || styleStroke === 'transparent') return false;
  return Boolean(styleStroke);
}

function isCutVisibleFromAttrs(el: Element): boolean {
  if (el.getAttribute('display') === 'none' || el.getAttribute('visibility') === 'hidden') return false;
  const opacity = el.getAttribute('opacity') ?? readStyleProperty(el.getAttribute('style') ?? '', 'opacity');
  if (opacity != null) {
    const parsed = parseFloat(opacity);
    if (Number.isFinite(parsed) && parsed <= 0) return false;
  }
  return true;
}

function hasPaintedFill(computed: CSSStyleDeclaration, el: Element): boolean {
  const fill = computed.fill;
  if (fill && fill !== 'none' && fill !== 'transparent') {
    const opacity = parseFloat(computed.fillOpacity);
    if (!Number.isFinite(opacity) || opacity > 0) return true;
  }
  return hasPaintedFillFromAttrs(el);
}

function hasPaintedStroke(computed: CSSStyleDeclaration, el: Element): boolean {
  const stroke = computed.stroke;
  if (stroke && stroke !== 'none' && stroke !== 'transparent') {
    const opacity = parseFloat(computed.strokeOpacity);
    if (!Number.isFinite(opacity) || opacity > 0) return true;
  }
  return hasPaintedStrokeFromAttrs(el);
}

function isCutVisible(computed: CSSStyleDeclaration, el: Element): boolean {
  if (computed.display === 'none' || computed.visibility === 'hidden') return false;
  const opacity = parseFloat(computed.opacity);
  if (Number.isFinite(opacity) && opacity <= 0) return false;
  return isCutVisibleFromAttrs(el);
}

function isMeasurableGeometry(element: Element, root: SVGSVGElement): boolean {
  let node: Element | null = element;
  while (node && node !== root) {
    if (NON_RENDERED_ANCESTOR_TAGS.has(node.tagName.toLowerCase())) return false;
    node = node.parentElement;
  }
  return true;
}

function geometryPathData(geom: Element): string | null {
  if (geom.tagName.toLowerCase() === 'path') {
    const d = geom.getAttribute('d');
    return d?.trim() ? d : null;
  }
  const converted = SVGPathCommander.shapeToPathArray(geom as Parameters<typeof SVGPathCommander.shapeToPathArray>[0]);
  if (!converted) return null;
  return SVGPathCommander.pathToString(converted);
}

function ringPerimeter(ring: PathD): number {
  if (ring.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < ring.length; i += 1) {
    total += Math.hypot(ring[i].x - ring[i - 1].x, ring[i].y - ring[i - 1].y);
  }
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first.x !== last.x || first.y !== last.y) {
    total += Math.hypot(first.x - last.x, first.y - last.y);
  }
  return total;
}

function sumPathsPerimeter(paths: PathsD): number {
  let total = 0;
  for (const ring of paths) {
    total += ringPerimeter(ring);
  }
  return total;
}

/** Sample baked (root-space) path data into closed rings for Clipper. */
function flattenBakedPathToRings(bakedPathData: string, flatness: number): PathsD {
  const subpaths = SVGPathCommander.splitPath(bakedPathData);
  const rings: PathsD = [];

  subpaths.forEach((subpath) => {
    const normalized = SVGPathCommander.normalizePath(subpath);
    const totalLen = SVGPathCommander.getTotalLength(normalized);
    if (!(totalLen > 0)) return;

    const step = Math.max(flatness, totalLen / 800);
    const count = Math.max(4, Math.ceil(totalLen / step));
    const ring: PathD = [];

    for (let i = 0; i <= count; i += 1) {
      const dist = (i / count) * totalLen;
      const pt = SVGPathCommander.getPointAtLength(normalized, dist);
      ring.push({ x: pt.x, y: pt.y });
    }

    if (ring.length >= 3) rings.push(ring);
  });

  return rings;
}

function flattenPathToRings(pathData: string, matrix: SvgMatrixLike, flatness: number): PathsD {
  const subpaths = SVGPathCommander.splitPath(pathData);
  const rings: PathsD = [];

  subpaths.forEach((subpath) => {
    const normalized = typeof subpath === 'string' ? subpath : SVGPathCommander.pathToString(subpath);
    const baked = transformPathDataWithMatrix(normalized, matrix);
    if (!baked) return;
    rings.push(...flattenBakedPathToRings(baked, flatness));
  });

  return rings;
}

function samplePathRing(pathInput: string, matrix: SvgMatrixLike, flatness: number): PathD | null {
  const baked = transformPathDataWithMatrix(pathInput, matrix);
  if (!baked) return null;

  const subpaths = SVGPathCommander.splitPath(baked);
  const rings: PathsD = [];

  subpaths.forEach((subpath) => {
    const bakedSubpath = typeof subpath === 'string' ? subpath : SVGPathCommander.pathToString(subpath);
    rings.push(...flattenBakedPathToRings(bakedSubpath, flatness));
  });

  if (rings.length === 0) return null;
  if (rings.length === 1) return rings[0];
  return rings.reduce((best, ring) => (ringPerimeter(ring) > ringPerimeter(best) ? ring : best));
}

function buildIdMap(root: SVGSVGElement): Map<string, Element> {
  const map = new Map<string, Element>();
  root.querySelectorAll('[id]').forEach((el) => {
    const id = el.getAttribute('id');
    if (id) map.set(id, el);
  });
  return map;
}

function flattenClipPathPolygons(
  clipPathEl: SVGClipPathElement,
  refElement: SVGGraphicsElement,
  root: SVGSVGElement,
  flatness: number,
  clipper: ClipperModule
): PathsD {
  const refToRoot = getGeometryToRootMatrix(refElement, root);
  const clipPathUnits = clipPathEl.getAttribute('clipPathUnits') ?? 'userSpaceOnUse';
  const unitsMatrix =
    clipPathUnits === 'objectBoundingBox' ? objectBoundingBoxMatrix(refElement) : IDENTITY_MATRIX;
  const clipPathMatrix = parseTransformOnRoot(root, clipPathEl.getAttribute('transform'));

  const rings: PathsD = [];
  clipPathEl.querySelectorAll(GEOMETRY_SELECTOR).forEach((node) => {
    if (!isGeometryTag(node)) return;
    const pathData = geometryPathData(asGeometryElement(node));
    if (!pathData) return;

    const childMatrix = parseTransformOnRoot(root, node.getAttribute('transform'));
    const matrix = multiplyMatrices(refToRoot, multiplyMatrices(clipPathMatrix, multiplyMatrices(unitsMatrix, childMatrix)));
    rings.push(...flattenPathToRings(pathData, matrix, flatness));
  });

  if (rings.length === 0) return [];
  if (rings.length === 1) return rings;
  return clipper.unionD(rings, clipper.FillRule.NonZero);
}

function applyClipToSubjects(subjects: PathsD, clipPolygons: PathsD, clipper: ClipperModule): PathsD {
  if (subjects.length === 0 || clipPolygons.length === 0) return [];
  const result: PathsD = [];
  for (const subject of subjects) {
    const clipped = clipper.intersectD([subject], clipPolygons, clipper.FillRule.NonZero);
    if (clipped.length > 0) result.push(...clipped);
  }
  return result;
}

function collectCutGeometry(
  root: SVGSVGElement,
  idMap: Map<string, Element>,
  flatness: number,
  clipper: ClipperModule
): { fillSubjects: PathsD; fillAnalyticLength: number; strokeLength: number } {
  const fillSubjects: PathsD = [];
  let fillAnalyticLength = 0;
  let strokeLength = 0;

  const walk = (node: Element, clipStack: PathsD[]) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    if (tag === 'defs' || tag === 'clippath' || tag === 'mask' || tag === 'metadata') return;

    let nextClipStack = clipStack;
    const clipRef =
      resolveUrlId(el.getAttribute('clip-path')) ??
      (() => {
        const style = el.getAttribute('style') ?? '';
        const match = style.match(/clip-path\s*:\s*url\s*\(\s*['"]?#([^'")\s]+)/i);
        return match?.[1] ?? null;
      })();

    if (clipRef) {
      const clipEl = idMap.get(clipRef);
      if (clipEl && isClipPathElement(clipEl) && isGraphicsElement(el)) {
        const clipPolys = flattenClipPathPolygons(clipEl, el, root, flatness, clipper);
        if (clipPolys.length > 0) {
          nextClipStack = [...clipStack, clipPolys];
        }
      }
    }

    if (isGeometryTag(el) && isMeasurableGeometry(el, root)) {
      const geom = asGeometryElement(el);
      const computed = getComputedStyle(el);
      if (!isCutVisible(computed, el)) {
        return;
      }

      const pathData = geometryPathData(geom);
      if (!pathData) return;

      const matrix = getGeometryToRootMatrix(geom, root);
      const hasFill = hasPaintedFill(computed, el);
      const hasStroke = hasPaintedStroke(computed, el);

      if (hasFill) {
        if (nextClipStack.length === 0) {
          fillAnalyticLength += measureTransformedPathLength(pathData, matrix);
        } else {
          let rings = flattenPathToRings(pathData, matrix, flatness);
          for (const clipPolys of nextClipStack) {
            rings = applyClipToSubjects(rings, clipPolys, clipper);
          }
          fillSubjects.push(...rings);
        }
      } else if (hasStroke) {
        if (nextClipStack.length > 0) {
          const ring = samplePathRing(pathData, matrix, flatness);
          if (ring) {
            let clipped = [ring];
            for (const clipPolys of nextClipStack) {
              clipped = applyClipToSubjects(clipped, clipPolys, clipper);
            }
            strokeLength += sumPathsPerimeter(clipped);
          }
        } else {
          strokeLength += measureTransformedPathLength(pathData, matrix);
        }
      }
      return;
    }

    for (const child of Array.from(el.children)) {
      walk(child, nextClipStack);
    }
  };

  for (const child of Array.from(root.children)) {
    walk(child, []);
  }

  return { fillSubjects, fillAnalyticLength, strokeLength };
}

function withMountedSvgRoot<T>(doc: Document, measure: (root: SVGSVGElement) => T): T | null {
  const parsedRoot = doc.documentElement;
  if (!parsedRoot || parsedRoot.nodeName.toLowerCase() !== 'svg') return null;

  const root = parsedRoot.cloneNode(true) as SVGSVGElement;
  root.style.cssText = MEASURE_MOUNT_STYLES;
  document.body.appendChild(root);

  try {
    return measure(root);
  } finally {
    document.body.removeChild(root);
  }
}

function measureMountedSvg(root: SVGSVGElement, viewBoxSize: number, clipper: ClipperModule): number {
  const flatness = Math.max(viewBoxSize / 1000, 0.01);
  const idMap = buildIdMap(root);
  const { fillSubjects, fillAnalyticLength, strokeLength } = collectCutGeometry(
    root,
    idMap,
    flatness,
    clipper
  );

  let fillLength = fillAnalyticLength;
  if (fillSubjects.length > 0) {
    fillLength += sumPathsPerimeter(fillSubjects);
  }

  return fillLength + strokeLength;
}

/** Vector cut length in viewBox units (inkex-style bake + clip intersect, all contours summed). */
export async function measureVectorCutLengthFromSvg(
  rawSvg: string,
  fallbackWidth: number,
  fallbackHeight: number
): Promise<number> {
  if (!rawSvg?.trim()) return 0;

  try {
    const clipper = await loadClipper();
    const doc = new DOMParser().parseFromString(rawSvg, 'image/svg+xml');
    if (doc.querySelector('parsererror')) return 0;

    const viewBox = parseSvgViewBox(rawSvg, fallbackWidth, fallbackHeight);
    const viewBoxSize = Math.max(viewBox.width, viewBox.height);

    const total = withMountedSvgRoot(doc, (root) => measureMountedSvg(root, viewBoxSize, clipper));
    return total != null && total > 0 ? total : 0;
  } catch {
    return 0;
  }
}
